/*
 * @Author: Teemo Ding
 * @Date: 2021-08-30 17:02:20
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2022-05-16 11:12:27
 * @FilePath: /unifyxp-v2-store-portal/src/pages/StoreVoucher/Detail/Summary/index.tsx
 */
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { Form } from 'antd';
import { get } from 'lodash';
import React from 'react';
import { useI18n } from 'src/hooks/useRouterChange';
import SummaryComponent from 'src/pages/SalesCannelVersion/SalesChannelDiscounts/Components/Summary';
import { EProductType } from 'src/pages/StoreVoucher/Detail/Components/ProductsApplyToRadioAndTable';
import { ECondition } from 'src/pages/StoreVoucher/Detail/VoucherSendTo';
import { EMethod } from 'src/pages/StoreVoucher/Detail/VoucherType';
import {
  i18nGet,
  replaceI18nVariables,
  handleOnFormatStoreTimeAndDate,
} from 'src/tools/tools';
import { EShipping, EnumStatus } from 'src/types/common/discount';
import css from './css.module.less';

interface IProps {
  status?: string;
  activeTime?: any;
  currentDb?: any;
  dbs?: any[];
  total?;
  used?;
  delivered?;
  remaining?;
  disabled?;
  noInfo?: boolean;
  isDraftArchive: boolean;
}
const Summary = ({
  status,
  activeTime,
  currentDb,
  dbs,
  total,
  used,
  delivered,
  remaining,
  disabled,
  noInfo,
  isDraftArchive,
}: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    beginDate = '',
    beginTime = '',
    endDate = '',
    endTime = '',
  } = activeTime;

  const db = dbs.find((i) => i.customerDatabaseUuid === currentDb);

  const renderSummaryData = (getFieldsValue) => {
    const begin = beginDate
      ? handleOnFormatStoreTimeAndDate(beginDate + ' ' + beginTime)
      : '';
    const end = endDate
      ? handleOnFormatStoreTimeAndDate(endDate + ' ' + endTime)
      : '';
    const {
      applyTo,
      currency,
      customerCondition,
      salesChannels = [],
      usageLimitation,
      voucherMethod,
      voucherQuantity: { limited = false },
      voucherType,
    } = getFieldsValue([
      'description',
      'applyTo',
      'currency',
      'customerCondition',
      'customerDatabase',
      'salesChannels',
      'usageLimitation',
      'voucherMethod',
      'voucherQuantity',
      'voucherType',
      'usageCondition',
    ]);
    const _salesChannels = (get(db, 'salesChannelCurrencies') || []).map(
      (i) => {
        const { salesChannelName, salesChannelUuid } = i;
        return { value: salesChannelUuid, label: salesChannelName };
      },
    );

    const displaySalesChannels = (salesChannels || []).reduce((pre, uuid) => {
      const salesChannel = _salesChannels.find((i) => i.value === uuid);
      if (salesChannel) {
        return { ...pre, [uuid]: salesChannel };
      } else {
        return pre;
      }
    }, {});
    const dbName = get(db, 'customerDatabaseName');
    const salesChannelName = (salesChannels || []).map((i) => {
      const name = get(displaySalesChannels[i], 'label');
      return name;
    });
    const dbLine = dbName
      ? `${getI18n('voucher.storeVoucherDetail.customerdatabaseis')} ${
          dbName || ''
        }`
      : '';

    const currencyLine = currency
      ? `${getI18n('voucher.storeVoucherDetail.currencyis')} ${currency || ''}`
      : '';
    const salesChannelLine =
      salesChannelName.length > 0
        ? `${getI18n(
            'voucher.storeVoucherDetail.salesChannelsare',
          )}${salesChannelName.join(' , ')}`
        : '';

    const {
      percentage,
      deduction,
      shippingDiscountType,
      fixedAmount,
      salesChannelGifts = {},
    } = voucherMethod;
    const freeItemsTip = Object.keys(salesChannelGifts).map((uuid, index) => {
      const name = get(
        displaySalesChannels[uuid],
        'label',
        `Sales Channel ${index + 1}`,
      );
      const target = salesChannelGifts[uuid] || [];
      let count = 0;
      target.forEach((gift) => {
        const { children } = gift;
        if (children) {
          count = count + children.length;
        } else {
          count = count + 1;
        }
      });

      return replaceI18nVariables(
        getI18n('voucher.storeVoucherDetail.namehascountitems'),
        name,
        count,
      );
    });
    const { combinedWithVoucher, combinedWithPromotion } =
      usageLimitation || {};
    const { flashSaleExcluded = '', conditionType = '' } = applyTo || {};
    const { conditionType: customerConditionType } = customerCondition;
    const discountValueApplies = {
      [EProductType.All]: getI18n('voucher.storeVoucherDetail.allproduct'),
      [EProductType.Specific]: getI18n(
        'voucher.storeVoucherDetail.specificproducts',
      ),
      [EProductType.NotSpecific]: getI18n(
        'voucher.storeVoucherDetail.allproductsspecific',
      ),
    };

    const discountMethodText = {
      [EMethod.Percentage]: `${replaceI18nVariables(
        getI18n('voucher.storeVoucherDetail.percentageoffappliesto'),
        percentage || '0',
        discountValueApplies[conditionType],
      )} ${
        flashSaleExcluded
          ? getI18n('voucher.storeVoucherDetail.excludingflashsaleitems')
          : ''
      }`,

      [EMethod.Reduction]: `${replaceI18nVariables(
        getI18n('voucher.storeVoucherDetail.deductionreductionamount'),
        deduction || '0',
      )} ${
        flashSaleExcluded
          ? getI18n('voucher.storeVoucherDetail.excludingflashsaleitems')
          : ''
      }`,

      [EMethod.Shipping]: `${
        shippingDiscountType == EShipping.Free
          ? getI18n('voucher.storeVoucherDetail.freeshippingto')
          : '$' +
            fixedAmount +
            getI18n('voucher.storeVoucherDetail.reductiononshippingrateto')
      }`,
      [EMethod.Gift]: freeItemsTip.join(' '),
    };

    const customerConditionText = {
      [ECondition.Registered]: getI18n(
        'voucher.storeVoucherDetail.forregisteredcustomers',
      ),
      [ECondition.Specific]: getI18n(
        'voucher.storeVoucherDetail.forspecificcustomers',
      ),
    };
    const summaryData = [
      ...(dbLine ? [dbLine] : []),
      ...(currencyLine ? [currencyLine] : []),
      ...(salesChannelLine ? [salesChannelLine] : []),

      ...(begin
        ? [
            `${
              end
                ? replaceI18nVariables(
                    getI18n('voucher.storeVoucherDetail.validwithenddate'),
                    begin,
                    end,
                  )
                : replaceI18nVariables(
                    getI18n('voucher.storeVoucherDetail.validnoenddate'),
                    begin,
                  )
            }`,
          ]
        : []),
      ...(voucherType ? [discountMethodText[voucherType]] : []),
      ...(combinedWithVoucher
        ? [getI18n('voucher.storeVoucherDetail.vouchercannotcombine1')]
        : []),
      ...(combinedWithPromotion
        ? [getI18n('voucher.storeVoucherDetail.vouchercannotcombine2')]
        : []),
      ...(customerConditionType
        ? [customerConditionText[customerConditionType]]
        : []),
      ...(isDraftArchive
        ? []
        : [
            limited
              ? replaceI18nVariables(
                  getI18n(
                    'voucher.storeVoucherDetail.totaldeliveredusedremaining',
                  ),
                  total,
                  delivered,
                  used,
                  remaining,
                )
              : replaceI18nVariables(
                  getI18n('voucher.storeVoucherDetail.deliveredused'),
                  delivered,
                  used,
                ),
          ]),
    ];
    return summaryData;
  };

  return (
    <div className={css.itemBlock}>
      <SectionWrapper
        titleClassName={css.titleContainer}
        containerClassName={css.sectionContainer}
        title={getI18n('general.summary')}>
        {!noInfo ? (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, getFieldsValue }) => {
              return (
                <SummaryComponent
                  name={getFieldValue('voucherNo')}
                  status={status || EnumStatus.Draft}
                  summaryContent={renderSummaryData(getFieldsValue)}
                />
              );
            }}
          </Form.Item>
        ) : (
          <div className={css.noInfo}>
            {getI18n('voucher.storeVoucherDetail.noinformationenteredyet')}
          </div>
        )}
      </SectionWrapper>
    </div>
  );
};

export default Summary;
